/*
 * @flow
 */
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { round } from 'lodash';
import { announce, Button, FlexCol, FlexRow, Heading, Select, SelectOption, useFlags } from 'sarsaparilla';
import StarDisplay from '../components/StarDisplay';
import { fetchAggregateSelector } from '../reducers/aggregate';
import { fetchAggregate as fetchAggregateAction } from '../actions/aggregate';
import {
  fetchPublicReviews as fetchPublicReviewsAction,
  resetPublicReviews as resetPublicReviewsAction,
} from '../actions/publicReviews';
import RatingBreakout from '../components/RatingBreakout';
import CellCoverage from '../components/CellCoverage';
import LocationReviews from '../components/LocationReviews';
import { publicReviewsSelector } from '../reducers/publicReviews';
import type { aggregateResponse } from '../model/aggregate';
import type { publicReview } from '../model/publicReview';
import type { flagRequest } from '../actions/reviewFlag';
import { postReviewFlag } from '../actions/reviewFlag';
import { postReviewVote } from '../actions/reviewVote';
import type { voteRequest } from '../actions/reviewVote';
import type { eventType } from '../model/utils';
import { REVIEW_SORT_KEYS } from '../constants/constants';

type parentFeatureFlags = {
  ratingsReviewsCampsiteLevelReviews: boolean,
};

type PropTypes = {
  location: {
    location_type: string,
    location_id: string,
  },
  startKey: string,
  reviewsLoading: number,
  reviews: Array<publicReview>,
  fetchPublicReviews: Function,
  resetPublicReviews: Function,
  loading: boolean,
  aggregate: ?aggregateResponse,
  fetchAggregate: Function,
  flagReview: Function,
  voteReview: Function,
  className: string,
  noHeading: boolean,
  parentReviewFlags: parentFeatureFlags,
};

type State = {
  sortKey: string,
  rating: string,
  reviewDisplayLength: number,
};

export class InventoryReviewsSectionComponent extends React.PureComponent<PropTypes, State> {
  static defaultProps = {
    className: '',
  };

  state = {
    sortKey: 'MOST_RECENT',
    rating: '',
    reviewDisplayLength: 3,
  };

  componentDidMount() {
    const {
      loading,
      aggregate,
      location,
      resetPublicReviews,
      reviewsLoading,
      reviewLocationId,
      reviewLocationType,
    } = this.props;
    const { sortKey, rating } = this.state;
    if (!reviewsLoading || location.location_id !== reviewLocationId || location.location_type !== reviewLocationType) {
      resetPublicReviews(
        {
          locationId: location.location_id,
          locationType: location.location_type,
          pageSize: 10,
          rating,
          sortKey,
          startKey: '',
        },
        this.isParentLocation()
      );
    }

    if (!loading || !aggregate || location.location_id !== aggregate.location_id) {
      this.props.fetchAggregate({
        locationType: location.location_type,
        locationId: location.location_id,
      });
    }
  }

  isParentLocation() {
    const {
      location: { location_type: locationType },
    } = this.props;
    switch (locationType) {
      case 'Campground':
        return this.props.parentReviewFlags.ratingsReviewsCampsiteLevelReviews;
      case 'Ticket Facility':
      case 'Timed Entry':
        return true;
      default:
        return false;
    }
  }

  handleSortChange({ target: { value } }: eventType) {
    const { resetPublicReviews, location } = this.props;
    const { rating, reviewDisplayLength } = this.state;
    resetPublicReviews(
      {
        locationId: location.location_id,
        locationType: location.location_type,
        pageSize: reviewDisplayLength === 3 ? 10 : reviewDisplayLength + 10,
        rating,
        sortKey: value,
        startKey: '',
      },
      this.isParentLocation()
    );
    const announceValue = REVIEW_SORT_KEYS[value] ? `by ${REVIEW_SORT_KEYS[value]}` : '';
    announce(`The reviews list is now sorted ${announceValue}`);
    this.setState({ sortKey: value });
  }

  handleRatingFilterChange(value: string) {
    const { resetPublicReviews, location } = this.props;
    const { sortKey } = this.state;
    resetPublicReviews(
      {
        locationId: location.location_id,
        locationType: location.location_type,
        pageSize: 10,
        sortKey: sortKey.includes('RATING') ? 'MOST_RECENT' : sortKey,
        rating: value,
        startKey: '',
      },
      this.isParentLocation()
    );
    announce(`Showing reviews with rating of ${value} stars`);
    this.setState({
      reviewDisplayLength: 3,
      sortKey: sortKey.includes('RATING') ? 'MOST_RECENT' : sortKey,
      rating: value,
    });
  }

  handleViewExpansion() {
    const { fetchPublicReviews, startKey, reviews, location } = this.props;
    const { reviewDisplayLength, rating, sortKey } = this.state;
    let newLength = reviewDisplayLength === 3 ? 10 : reviewDisplayLength + 10;
    if (!startKey && newLength > reviews.length) {
      newLength = reviews.length;
    }
    this.setState({
      reviewDisplayLength: newLength,
    });
    if (startKey !== '') {
      fetchPublicReviews(
        {
          locationId: location.location_id,
          locationType: location.location_type,
          sortKey,
          rating,
          startKey,
          pageSize: 10,
        },
        this.isParentLocation()
      );
    }
  }

  handleReviewFlag(request: flagRequest) {
    const { flagReview, resetPublicReviews, reviews, location } = this.props;
    const { sortKey, rating } = this.state;
    flagReview(request)
      .then(() => {
        resetPublicReviews(
          {
            locationId: location.location_id,
            locationType: location.location_type,
            sortKey,
            rating,
            pageSize: reviews.length < 10 ? 10 : reviews.length,
            startKey: '',
          },
          this.isParentLocation()
        );
      })
      .catch(() => {});
  }

  handleReviewVote(request: voteRequest) {
    const { voteReview } = this.props;
    voteReview(request)
      .then(() => {
        localStorage.setItem(request.reservation_id, request.is_helpful.toString());
        this.forceUpdate();
      })
      .catch(() => {});
  }

  render() {
    const { aggregate, loading, startKey, reviews, reviewsLoading, className, noHeading } = this.props;
    const { reviewDisplayLength, sortKey, rating } = this.state;
    if (loading || !aggregate || !aggregate.location_id || aggregate.number_of_ratings <= 0) {
      return null;
    }
    return (
      <div
        id="rating-review-inventory-review-section"
        className={`rating-review-inventory-review-section-wrapper ${className}`}
      >
        {!noHeading && (
          <Heading
            headingLevel={2}
            appearance="h5"
            id="rating-review-inventory-review-section-heading"
            className="mb-2"
          >
            Guest Reviews ({aggregate.number_of_ratings})
          </Heading>
        )}
        <FlexRow>
          <FlexCol lg={8}>
            <FlexRow justifyContent="end">
              <FlexCol sm={6} lg={5}>
                <Select
                  label="Sort By"
                  isLabelVisible={false}
                  value={sortKey}
                  onChange={this.handleSortChange.bind(this)}
                  id="reviews-sort"
                >
                  <SelectOption value="MOST_RECENT">Sort by: Most Recent</SelectOption>
                  <SelectOption value="MOST_HELPFUL">Sort by: Most Helpful</SelectOption>
                  {rating === '' && <SelectOption value="HIGHEST_RATING">Sort by: Highest Rating</SelectOption>}
                  {rating === '' && <SelectOption value="LOWEST_RATING">Sort by: Lowest Rating</SelectOption>}
                </Select>
              </FlexCol>
            </FlexRow>
            <LocationReviews
              displayLength={reviewDisplayLength}
              loading={reviewsLoading}
              reviews={reviews}
              voteReview={this.handleReviewVote.bind(this)}
              flagReview={this.handleReviewFlag.bind(this)}
            />
            <FlexRow>
              {!!reviews && (startKey !== '' || reviews.length > reviewDisplayLength) && (
                <Button onClick={this.handleViewExpansion.bind(this)} appearance="tertiary">
                  See More Reviews
                </Button>
              )}
            </FlexRow>
          </FlexCol>
          <FlexCol className="left-rail" lg={4} order="first">
            <div className="rating-wrap">
              {round(aggregate.average_rating, 1)}
              <StarDisplay value={aggregate.average_rating} />
            </div>
            <RatingBreakout
              total={aggregate.number_of_ratings}
              starCounts={aggregate.star_counts}
              value={rating}
              onChange={this.handleRatingFilterChange.bind(this)}
            />
            {!!aggregate.aggregate_cell_coverage_ratings && (
              <CellCoverage cellRatings={aggregate.aggregate_cell_coverage_ratings} />
            )}
          </FlexCol>
        </FlexRow>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { loading, data: aggregate } = fetchAggregateSelector(state);
  const {
    data: reviews,
    startKey,
    loading: reviewsLoading,
    location_id: reviewLocationId,
    location_type: reviewLocationType,
  } = publicReviewsSelector(state);
  const { locationType, locationId } = ownProps;
  return {
    location: { location_id: locationId, location_type: locationType },
    startKey,
    loading,
    reviews,
    reviewsLoading,
    reviewLocationId,
    reviewLocationType,
    aggregate,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAggregate: fetchAggregateAction,
      fetchPublicReviews: fetchPublicReviewsAction,
      resetPublicReviews: resetPublicReviewsAction,
      flagReview: postReviewFlag,
      voteReview: postReviewVote,
    },
    dispatch
  );

const InventoryReviewsSectionContainer = connect(mapStateToProps, mapDispatchToProps)(InventoryReviewsSectionComponent);

export { InventoryReviewsSectionContainer };

export default function InventoryReviewsSection(props) {
  const { ratingsReviewsCampsiteLevelReviews } = useFlags();
  const parentReviewFlags = {
    ratingsReviewsCampsiteLevelReviews,
  };
  return <InventoryReviewsSectionContainer parentReviewFlags={parentReviewFlags} {...props} />;
}
